import React, { useState } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from 'react-router-dom';
import { MediaRenderer } from './ImagePlaceholder';
import { ArrowRightIcon } from './ArrowRightIcon';
import type { FeaturedVenueCardProps } from '@/features/artists/types/types';

/**
 * Featured venue card component
 */
export const FeaturedVenueCard: React.FC<FeaturedVenueCardProps> = ({ artist }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/venues/${artist.business.handle}`);
  };

  const mediaVariants = {
    initial: { opacity: 0, scale: 1.05 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.05 },
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      className="group relative bg-gray-900/50 border border-gray-800 rounded-3xl overflow-hidden flex flex-col md:flex-row transition-all duration-300 hover:border-blue-500/50 cursor-pointer"
    >
      <div className="absolute inset-0 z-0">
        <AnimatePresence initial={false}>
          {!isHovered ? (
            <motion.div key="image" {...mediaVariants} className="h-full w-full">
              <MediaRenderer 
                mediaId={artist.business.card_image_id} 
                alt={`${artist.business.name} venue`}
                className="!rounded-none h-full w-full object-cover" 
              />
            </motion.div>
          ) : (
            <motion.div key="video" {...mediaVariants} className="h-full w-full">
              <MediaRenderer 
                mediaId={artist.business.card_video_id} 
                alt={`${artist.business.name} video`}
                className="!rounded-none h-full w-full object-cover"
                isVideo={true}
                autoPlay={true}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/70 group-hover:bg-black/80 transition-colors duration-300"></div>
      </div>

      <div className="relative z-10 p-8 flex flex-col w-full">
        <h3 className="text-2xl font-serif font-medium text-white mb-2">
          {artist.business.name}
        </h3>
        <p className="text-gray-400 mb-4">
          {`${artist.business.location.city}, ${artist.business.location.country}`}
        </p>
        <p className="text-gray-300 leading-relaxed flex-grow mb-6 max-w-lg">
          {artist.business.description}
        </p>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
          className="group mt-auto w-full md:w-fit flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-full font-medium transition-all duration-300 hover:bg-blue-500 transform hover:scale-102"
        >
          <span className="flex items-center gap-2">
            View Venue & Book <ArrowRightIcon />
          </span>
        </button>
      </div>
    </div>
  );
};
